/**
 * If this error is thrown, it means a developer messed up
 */
export class DevError extends Error {
  constructor(message?: string) {
    super(message)
    this.name = 'Developer Error'
  }
}

/**
 * When this type of error is thrown, the error message will be displayed verbatim to the user
 */
export class MessageError extends Error {
  constructor(message?: string) {
    super(message)
    this.name = 'Error'
  }
}

/**
 * The record a details page was asked for does not exist for this user. Three
 * things look identical from the client (a null `*_by_pk`): the record was
 * deleted, the link carries an id from before the multi-tenant cutover
 * (2026-06-27, every id moved to `oldId + clientId * 500,000,000`, so old
 * bookmarks still name the old id), or it belongs to another tenant and
 * Hasura's row filter hid it. Expected, not a bug: the global handler tells
 * the user, returns them to the list page and reports it at severity info
 * under its own class, one Bugsnag error per entity.
 */
export class NotFoundError extends Error {
  constructor(
    /** what was looked up, e.g. 'study'; used in the message and for grouping */
    readonly entity: string,
    /** the id that was looked up, usually from the URL */
    readonly id: number | string | undefined,
    /** where to send the user, e.g. ['home', 'studies']; stays put when omitted */
    readonly listRoute?: string[],
  ) {
    super(`${entity} not found`)
    this.name = 'NotFoundError'
  }
}

/**
 * One or more tests could not be re-evaluated from the scanner page. The
 * message names them for the user; the class keeps them together in the
 * issue tracker (the server has no tracker of its own and returns the reason
 * per test instead of failing the whole request).
 */
export class ReevaluationError extends MessageError {
  constructor(
    message: string,
    readonly failures: { id: number; error: string }[],
  ) {
    super(message)
    this.name = 'ReevaluationError'
  }
}
